import { useCallback } from "react"
import { batch } from "@preact/signals-react"

import { useToast } from "@hooks"
import { MODE_BY_SETUP } from "../@constants"
import {
    customerSetup,
    havePendingChanges,
    mainSitesData,
    modeChangeAcknowledged,
    pricingConfigMode,
    reloadPricingConfigData,
    rrspData
} from "../@signals"

const PENDING_WRITE_MESSAGE = 'Staged — press Save to apply it'

/** Same market? Compared case-insensitively, because the column's collation is: `es_ca` and `ES_CA`
 * are the *same* primary key for the database, so they must be the same row here too. */
const sameMarket = (a, b) => (a || '').toUpperCase() === (b || '').toUpperCase()

/** Stage a change in the table without persisting it.
 *
 * Nothing here talks to the server: rows are marked (`pending: new | update | delete`) and the save
 * bar sends **only the marked ones** (`usePricingConfigSave`). Two reasons for staging instead of
 * saving row by row: a mode change and its rows are a single decision, and an untouched RRSP row
 * belongs to other customers too, so re-sending it is a chance to move a price nobody asked to move.
 */
export default function usePricingConfigDraft() {

    const { showToast } = useToast()

    const notifyStaged = useCallback(() => {
        havePendingChanges.value = true
        showToast.warning(PENDING_WRITE_MESSAGE)
    }, [])

    // One main site per market — that is the table's own primary key, so a market already
    // configured is replaced, never duplicated. Matching with `===` would stage a second row for a
    // market already there, and the upsert would collapse both into one: the screen would have
    // shown a duplicate that never existed.
    const stageMainSite = useCallback(row => {
        const isKnownMarket = mainSitesData.value.some(item => sameMarket(item.country, row.country))
        mainSitesData.value = isKnownMarket
            ? mainSitesData.value.map(item => sameMarket(item.country, row.country)
                ? { ...item, ...row, pending: 'update' }
                : item)
            : [...mainSitesData.value, { ...row, pending: 'new' }]
        notifyStaged()
    }, [])

    const stageRrspRow = useCallback(row => {
        const isExisting = !!row.rrspID
        rrspData.value = isExisting
            ? rrspData.value.map(item => item.rrspID === row.rrspID
                ? { ...item, ...row, pending: 'update' }
                : item)
            : [...rrspData.value, { ...row, pending: 'new' }]
        notifyStaged()
    }, [])

    const stageMainSiteRemoval = useCallback(row => {
        mainSitesData.value = mainSitesData.value.map(item => sameMarket(item.country, row.country)
            ? { ...item, pending: 'delete' }
            : item)
        notifyStaged()
    }, [])

    const stageRrspRemoval = useCallback(row => {
        rrspData.value = rrspData.value.map(item => item.rrspID === row.rrspID
            ? { ...item, pending: 'delete' }
            : item)
        notifyStaged()
    }, [])

    /** Drop every staged row and put the selector back where the customer actually is.
     *
     * Lives here rather than in `usePricingConfigSave` because it is a draft operation, and two
     * places need it: the save bar's *Discard*, and the warning's *Keep current* — going back to the
     * original mode leaves rows staged for a branch that is no longer on screen, so they could only
     * be saved unseen.
     */
    const discardChanges = useCallback(() => batch(() => {
        havePendingChanges.value = false
        modeChangeAcknowledged.value = false
        // Discarding a mode change and leaving the selector on the new one would look like it had
        // been applied.
        const currentMode = MODE_BY_SETUP[customerSetup.value?.mode]
        if (currentMode) pricingConfigMode.value = currentMode
        // The drafts only live in these signals, so re-reading is enough to drop them.
        reloadPricingConfigData.value = true
    }), [])

    return {
        stageMainSite,
        stageMainSiteRemoval,
        stageRrspRow,
        stageRrspRemoval,
        discardChanges
    }
}
